import React, { useState, useEffect, useMemo } from 'react';
import ReactTable from "react-table";
import "react-table/react-table.css";

const SeccionMetasVenta = ({ sucursalActiva, sectorActivo, infoFiltros }) => {
    const [isOpen, setIsOpen] = useState(false);
    // defaults to current month
    const currentMonthStr = (new Date().getMonth() + 1).toString();
    const [mesActivo, setMesActivo] = useState(currentMonthStr);
    const [dataMetas, setDataMetas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    const anioActivo = '2026'; // año en curso según requerimiento

    useEffect(() => {
        const fetchMetas = async () => {
            setLoading(true);
            try {
                let qs = `?anio=${anioActivo}`;
                if (sucursalActiva) qs += `&sucursal_id=${sucursalActiva}`;
                if (mesActivo) qs += `&mes=${mesActivo}`;
                if (sectorActivo) qs += `&sector=${encodeURIComponent(sectorActivo)}`;

                const res = await fetch(`/api/dashboard-ventas/metas-productos${qs}`);
                const json = await res.json();
                if (json.success) {
                    setDataMetas(json.data || []);
                }
            } catch (error) {
                console.error("Error al cargar metas:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMetas();
    }, [anioActivo, sucursalActiva, sectorActivo, mesActivo]);

    // Opciones de mes
    const mesOptions = [
        { value: '1', label: 'Ene' }, { value: '2', label: 'Feb' }, { value: '3', label: 'Mar' },
        { value: '4', label: 'Abr' }, { value: '5', label: 'May' }, { value: '6', label: 'Jun' },
        { value: '7', label: 'Jul' }, { value: '8', label: 'Ago' }, { value: '9', label: 'Sep' },
        { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dic' }
    ];

    // Cálculos
    const formatNumber = (val) => new Intl.NumberFormat('es-PE').format(val);

    const {
        metaTotal,
        vendidoTotal,
        faltaVender,
        diasTranscurridos,
        diasTotales,
        ventaDiariaNecesaria,
        proyeccion
    } = useMemo(() => {
        if (!dataMetas || dataMetas.length === 0) {
            return { metaTotal: 0, vendidoTotal: 0, faltaVender: 0, diasTranscurridos: 0, diasTotales: 0, ventaDiariaNecesaria: 0, proyeccion: 0 };
        }

        const mTotal = dataMetas.reduce((sum, item) => sum + Math.round(item.unidades_ant * 1.10), 0);
        const vTotal = dataMetas.reduce((sum, item) => sum + (Number(item.unidades_act) || 0), 0);
        const falta = Math.max(mTotal - vTotal, 0);

        const hoy = new Date();
        const mesNum = parseInt(mesActivo);
        const anioNum = parseInt(anioActivo);
        const dTotales = new Date(anioNum, mesNum, 0).getDate();

        let dTrans = 0;
        if (hoy.getFullYear() > anioNum || (hoy.getFullYear() === anioNum && hoy.getMonth() + 1 > mesNum)) {
            dTrans = dTotales;
        } else if (hoy.getFullYear() === anioNum && hoy.getMonth() + 1 === mesNum) {
            dTrans = hoy.getDate();
        } else {
            dTrans = 0;
        }

        const dRestantes = Math.max(dTotales - dTrans, 0);
        const vDiariaNecesaria = dRestantes > 0 ? (falta / dRestantes) : 0;
        const proy = dTrans > 0 ? (vTotal / dTrans) * dTotales : 0;

        return {
            metaTotal: mTotal,
            vendidoTotal: vTotal,
            faltaVender: falta,
            diasTranscurridos: dTrans,
            diasTotales: dTotales,
            ventaDiariaNecesaria: vDiariaNecesaria,
            proyeccion: proy
        };
    }, [dataMetas, mesActivo, anioActivo]);

    // Filtrar tabla por búsqueda
    const dataFiltrada = useMemo(() => {
        if (!busqueda) return dataMetas;
        const searchLower = busqueda.toLowerCase();
        return dataMetas.filter(item =>
            (item.producto && item.producto.toLowerCase().includes(searchLower)) ||
            (item.presentacion && item.presentacion.toLowerCase().includes(searchLower))
        );
    }, [dataMetas, busqueda]);

    return (
        <div className="shadow-sm mb-4" style={{ border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
            <div
                className="card-header border-bottom p-4 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#35605a', borderRadius: '1.25rem' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div>
                    <h4 className="font-weight-bold text-white m-0">
                        <i className="fas fa-bullseye mr-2 text-white"></i> Metas de Ventas {anioActivo}
                    </h4>
                    {infoFiltros && <div className="mt-1"><small className="text-white"><i className="fas fa-filter mr-1"></i> Filtrando por: {infoFiltros}</small></div>}
                    <p className="text-white mb-0 mt-1">Monitoreo de objetivos con regla de +10% sobre el año anterior</p>
                </div>
                <button className='btn btn-light btn-sm rounded-circle'>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                </button>
            </div>

            {isOpen && (
                <div className="card-body p-4">
                    {/* Filtro de Mes Interno */}
                    <div className="d-flex align-items-center flex-wrap mb-2 border-bottom">
                        <span className="text-muted font-weight-bold mr-3" style={{ fontSize: '0.85rem', textTransform: 'uppercase' }}>MES DE META</span>
                        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                            {mesOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    className={`btn btn-sm rounded-pill px-3 ${mesActivo === opt.value ? 'btn-verde-mfp font-weight-bold shadow-sm' : 'btn-light border text-dark'}`}
                                    onClick={() => setMesActivo(opt.value)}
                                    disabled={loading}
                                    style={{ fontSize: '14px' }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 6 Tarjetas de Resumen KPI */}
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="row mb-3">
                                {/* 1. Meta Total */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                        <div className="card-body p-3 text-center">
                                            <h6 className="text-white font-weight-bold mb-2" style={{ fontSize: '0.75rem' }}>META TOTAL DEL MES (+10%)</h6>
                                            <h4 className="font-weight-bold text-white mb-0">{formatNumber(metaTotal)}</h4>
                                            <small className="text-white">Unidades</small>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. Vendido hasta ahora */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                        <div className="card-body p-3 text-center">
                                            <h6 className="text-white font-weight-bold mb-2" style={{ fontSize: '0.75rem' }}>VENDIDO HASTA AHORA</h6>
                                            <h4 className="font-weight-bold text-white mb-0">{formatNumber(vendidoTotal)}</h4>
                                            <small className="text-white">Unidades</small>
                                        </div>
                                    </div>
                                </div>

                                {/* 3. Falta por vender */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                        <div className="card-body p-3 text-center">
                                            <h6 className="text-white font-weight-bold mb-2" style={{ fontSize: '0.75rem' }}>FALTA POR VENDER</h6>
                                            <h4 className="font-weight-bold text-white mb-0">{formatNumber(faltaVender)}</h4>
                                            <small className="text-white">Unidades</small>
                                        </div>
                                    </div>
                                </div>

                                {/* 4. Días transcurridos */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                        <div className="card-body p-3 text-center">
                                            <h6 className="text-white font-weight-bold mb-2" style={{ fontSize: '0.75rem' }}>DÍAS TRANSCURRIDOS</h6>
                                            <h4 className="font-weight-bold text-white mb-0">{diasTranscurridos} <span style={{ fontSize: '1rem', color: '#6c7d72ff' }}>/ {diasTotales}</span></h4>
                                            <small className="text-white">Días del mes</small>
                                        </div>
                                    </div>
                                </div>

                                {/* 5. Venta diaria necesaria */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                        <div className="card-body p-3 text-center">
                                            <h6 className="text-white font-weight-bold mb-2" style={{ fontSize: '0.75rem' }}>VENTA DIARIA NECESARIA</h6>
                                            <h4 className="font-weight-bold text-white mb-0">{formatNumber(Math.ceil(ventaDiariaNecesaria))}</h4>
                                            <small className="text-white">Unid. / día para meta</small>
                                        </div>
                                    </div>
                                </div>

                                {/* 6. Proyección a fin de mes */}
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="card shadow-sm border h-100 bg-light rounded" style={{ backgroundColor: '#212529', color: '#fff' }}>
                                        <div className="card-body p-3 text-center bg-dark text-white rounded">
                                            <h6 className="font-weight-bold mb-2 text-light" style={{ fontSize: '0.75rem' }}>PROYECCIÓN FIN MES</h6>
                                            <h4 className="font-weight-bold mb-0">{formatNumber(Math.round(proyeccion))}</h4>
                                            <small className="text-light">Unidades estimadas</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Barra de Progreso General */}
                            <div className="mb-4 p-3 border rounded shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="font-weight-bold text-dark mb-0">Avance General de la Meta del Mes</h6>
                                </div>
                                <div className="progress mb-3 shadow-sm" style={{ height: '25px', borderRadius: '15px', backgroundColor: '#e9ecef' }}>
                                    <div
                                        className={`progress-bar progress-bar-striped progress-bar-animated ${vendidoTotal >= metaTotal && metaTotal > 0 ? 'bg-success' : 'bg-info'}`}
                                        role="progressbar"
                                        style={{ width: `${Math.min((vendidoTotal / (metaTotal || 1)) * 100, 100)}%` }}
                                    >
                                        <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                            {((vendidoTotal / (metaTotal || 1)) * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap align-items-center justify-content-between mt-2 pt-3 border-top">
                                    {/* Estado de la meta con indicador visual */}
                                    <div className="d-flex align-items-center mb-2 mb-sm-0">
                                        <span
                                            className={`badge badge-pill px-3 py-2 font-weight-bold d-flex align-items-center`}
                                            style={{
                                                fontSize: '0.85rem',
                                                backgroundColor: vendidoTotal >= metaTotal && metaTotal > 0 ? '#d4edda' : '#f8d7da',
                                                color: vendidoTotal >= metaTotal && metaTotal > 0 ? '#155724' : '#721c24',
                                                border: `1px solid ${vendidoTotal >= metaTotal && metaTotal > 0 ? '#c3e6cb' : '#f5c6cb'}`
                                            }}
                                        >
                                            <i className={`fas ${vendidoTotal >= metaTotal && metaTotal > 0 ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} style={{ fontSize: '1rem' }}></i>
                                            {vendidoTotal >= metaTotal && metaTotal > 0 ? 'Meta Cumplida ✓' : 'Meta No Cumplida'}
                                        </span>
                                    </div>

                                    {/* Métricas con mejor jerarquía visual */}
                                    <div className="d-flex flex-wrap align-items-center">
                                        <div className="d-flex align-items-center mr-4 mb-2 mb-sm-0">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm" style={{ width: '32px', height: '32px', backgroundColor: '#fff', border: '1px solid #e9ecef' }}>
                                                <i className="fas fa-box text-secondary" style={{ fontSize: '0.85rem' }}></i>
                                            </div>
                                            <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                                Vendidas: <strong className="text-dark ml-1" style={{ fontSize: '1rem' }}>{formatNumber(vendidoTotal)}</strong>
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center mb-2 mb-sm-0">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm" style={{ width: '32px', height: '32px', backgroundColor: '#fff', border: '1px solid #e9ecef' }}>
                                                <i className="fas fa-bullseye text-secondary" style={{ fontSize: '0.85rem' }}></i>
                                            </div>
                                            <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                                Meta: <strong className="text-dark ml-1" style={{ fontSize: '1rem' }}>{formatNumber(metaTotal)}</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Buscador de Producto */}
                            <div className="mb-3 d-flex justify-content-end align-items-center">
                                <label className="text-muted font-weight-bold mr-3 mb-0" style={{ fontSize: '0.85rem' }}>
                                    <i className="fas fa-boxes mr-2"></i> {dataFiltrada.length} productos encontrados
                                </label>
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text bg-white"><i className="fas fa-search"></i></span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control border-left-0"
                                        placeholder="Buscar producto..."
                                        value={busqueda}
                                        onChange={(e) => setBusqueda(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Tabla de Metas */}
                            <div className="mt-3 rounded shadow-sm">
                                {dataFiltrada.length === 0 ? (
                                    <div className="text-center p-4 text-muted">No hay datos de metas para mostrar.</div>
                                ) : (
                                    <ReactTable
                                        data={dataFiltrada}
                                        columns={[
                                            { Header: "Producto", accessor: "producto", filterable: true },
                                            { Header: "Presentación", accessor: "presentacion", filterable: true },
                                            { Header: "Unid. Año Anterior", accessor: "unidades_ant", filterable: true, Cell: props => <div className="text-right text-muted">{props.value}</div> },
                                            { Header: "Meta (+10%)", accessor: "meta", filterable: true, Cell: props => <div className="text-right font-weight-bold text-primary">{props.value}</div> },
                                            { Header: "Vendido Este Año", accessor: "unidades_act", filterable: true, Cell: props => <div className="text-right font-weight-bold text-dark">{props.value}</div> },
                                            { Header: "% Avance", accessor: "avance", filterable: true, Cell: props => (<div className="text-right"> <span className={props.value >= 100 ? 'text-success font-weight-bold' : 'text-warning'}> {props.value}% </span> </div>) },
                                            { Header: "Unid. Faltantes", accessor: "faltantes", filterable: true, Cell: props => <div className="text-right">{props.value}</div> },
                                            {
                                                Header: "Unid./día neces.", id: "unid_dia", filterable: true, accessor: d => {
                                                    if (d.faltantes <= 0) return 0;
                                                    if (!mesActivo || mesActivo === 'ALL' || mesActivo === '') return '-';
                                                    const anio = parseInt(anioActivo);
                                                    const meses = mesActivo.split(',').map(Number);
                                                    const mes = meses[meses.length - 1];
                                                    const hoy = new Date();
                                                    const anioActual = hoy.getFullYear();
                                                    const mesActual = hoy.getMonth() + 1;

                                                    if (anio < anioActual || (anio === anioActual && mes < mesActual)) return '-';
                                                    if (anio > anioActual || (anio === anioActual && mes > mesActual)) {
                                                        const diasMes = new Date(anio, mes, 0).getDate();
                                                        return (d.faltantes / diasMes).toFixed(1);
                                                    }
                                                    const diasRestantes = new Date(anio, mes, 0).getDate() - hoy.getDate() + 1;
                                                    if (diasRestantes <= 0) return '-';
                                                    return (d.faltantes / diasRestantes).toFixed(1);
                                                }, Cell: props => <div className="text-center font-weight-bold">{props.value}</div>
                                            },
                                            { Header: "Estado", accessor: "logrado", filterable: true, Cell: props => (<div className="text-center"> <span className={`badge badge-pill ${props.value ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.85em', padding: '0.4em 0.6em' }}> {props.value ? 'Logrado' : 'Pendiente'} </span> </div>) }
                                        ]}
                                        defaultPageSize={10}
                                        className="-striped -highlight"
                                        previousText="Anterior"
                                        nextText="Siguiente"
                                        loadingText="Cargando..."
                                        noDataText="No hay datos"
                                        pageText="Página"
                                        ofText="de"
                                        rowsText="filas"
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default SeccionMetasVenta;
